import React, { createContext, useContext, useState, useCallback } from "react";

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem("arc_user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setLoggedInUser = useCallback((userData) => {
    setUser(userData);
    if (userData) {
      sessionStorage.setItem("arc_user", JSON.stringify(userData));
    } else {
      sessionStorage.removeItem("arc_user");
    }
  }, []);

  const clearUser = useCallback(() => {
    setUser(null);
    sessionStorage.removeItem("arc_user");
  }, []);

  return (
    <UserContext.Provider value={{ user, setLoggedInUser, clearUser }}>
      {children}
    </UserContext.Provider>
  );
}

/**
 * useUser — hook to get current logged-in user anywhere in the app.
 *
 * const { email, displayName } = useUser();
 *
 * email        — user's email e.g. "test1@company.com"
 * displayName  — "First Last"
 * user         — full { email, firstName, lastName } object
 */
export function useUser() {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within UserProvider");
  const { user, setLoggedInUser, clearUser } = context;
  return {
    user,
    email: user?.email || "system",
    displayName: user
      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
      : "System",
    setLoggedInUser,
    clearUser,
  };
}

export default UserContext;
